import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  GENERAL_ORDER_DISPLAY_LABEL,
  initialPaymentDraft,
  isOrderOptionalRequestType,
  isValidNewOperationRequestState,
  OPERATION_REQUEST_MODES,
  QUICK_REQUEST_TYPE_BLOCKS,
} from "../operationRequestConstants.js";
import { resolvePickerOrderDisplayNumber } from "../pickerOrderDisplayResolver.js";
import { useOperationRequestUi } from "../OperationRequestUiContext.jsx";
import { fetchOperationRequestOrderContext, createOperationRequest } from "../operationRequestsApi.js";
import { resolveOperationRequestOrderContext } from "../resolveOperationRequestOrderContext.js";
import { afterOperationRequestCreateSuccess } from "../operationRequestAfterCreateSuccess.js";
import {
  saveNameChangeOperationDraft,
  clearNameChangeOperationDraft,
} from "../operationRequestNameChangeDraft.js";
import { errorMessage, showSuccessToast } from "../../../services/swalService.js";
import {
  computePassengerNameChangePayment,
  buildNameChangeOperationMessage,
  logNameChangePaymentTraceDev,
  NAME_CHANGE_PAYMENT_REASON,
  NAME_CHANGE_EXCHANGE_SOURCE,
  NAME_CHANGE_HYP_TRANSACTION_DESCRIPTION,
} from "../../../utils/operationRequestNameChange.js";
import OperationRequestPage from "../workspace/OperationRequestPage.jsx";
import { useAppContext } from "../../../contexts/AppContext.js";
import { ApiReqBaseUrl } from "../../../assets/GlobalVariables.js";
import {
  extractPrimaryPaymentUrlFromAgentPaymentResponse,
  resolveAgentIdForPaymentBody,
  resolveOperationRequestPaymentEndpoint,
} from "../operationRequestAgentPaymentLink.js";

export default function NewOperationRequestPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currencyRate } = useAppContext();
  const usdIlsFromApp = currencyRate?.USD;
  const { openOperationRequest, showOperationRequestSuccess } = useOperationRequestUi();

  const routeState = location.state;
  const valid = isValidNewOperationRequestState(routeState);

  const mode = valid ? routeState.mode : null;
  const selectedOrder = valid ? routeState.selectedOrder || null : null;
  const initialRequestType = valid ? routeState.requestType ?? null : null;
  const hasLinkedOrder = Boolean(
    selectedOrder && String(selectedOrder.id || selectedOrder.orderId || "").trim()
  );

  const [requestType, setRequestType] = useState(initialRequestType);
  const [message, setMessage] = useState("");
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [paymentDraft, setPaymentDraft] = useState(initialPaymentDraft);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLinkBusy, setPaymentLinkBusy] = useState(false);
  const [enrichedPayload, setEnrichedPayload] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [nameChangePassengerId, setNameChangePassengerId] = useState(null);
  const [nameChangeNewFirst, setNameChangeNewFirst] = useState("");
  const [nameChangeNewLast, setNameChangeNewLast] = useState("");
  const nameChangeRestoreRef = useRef(false);

  const mergedOrder = useMemo(() => {
    if (!selectedOrder) return null;
    const ep = enrichedPayload;
    if (!ep?.rawOrder || typeof ep.rawOrder !== "object") return selectedOrder;
    const prevRaw =
      selectedOrder.raw && typeof selectedOrder.raw === "object" && !Array.isArray(selectedOrder.raw)
        ? selectedOrder.raw
        : {};
    return {
      ...selectedOrder,
      raw: { ...prevRaw, ...ep.rawOrder },
      _enrichedSourceKind: ep.sourceKind,
    };
  }, [selectedOrder, enrichedPayload]);

  const operationContext = useMemo(
    () =>
      mergedOrder
        ? resolveOperationRequestOrderContext(mergedOrder, {
            emitTrace: Boolean(import.meta.env?.DEV),
            usdIlsRateFromApp: usdIlsFromApp,
          })
        : null,
    [mergedOrder, usdIlsFromApp]
  );

  const isNameChange = requestType === "name_change";
  const passengersForNameChange = operationContext?.passengers ?? [];
  const selectedNamePassenger = useMemo(
    () => passengersForNameChange.find((p) => p.id === nameChangePassengerId) ?? null,
    [passengersForNameChange, nameChangePassengerId]
  );

  useEffect(() => {
    if (requestType !== "name_change") {
      setNameChangePassengerId(null);
      setNameChangeNewFirst("");
      setNameChangeNewLast("");
    }
  }, [requestType]);

  useEffect(() => {
    if (!valid || !hasLinkedOrder || !selectedOrder) return undefined;
    let cancelled = false;
    const oid = String(selectedOrder.id || selectedOrder.orderId || "").trim();
    if (!oid) return undefined;

    setContextLoading(true);
    setEnrichedPayload(null);
    (async () => {
      try {
        const data = await fetchOperationRequestOrderContext({
          orderId: oid,
          orderType: selectedOrder.rawOrderType || selectedOrder.orderType,
          orderCategory: selectedOrder.category,
        });
        if (cancelled) return;
        if (data?.ok && data.context) {
          setEnrichedPayload(data.context);
        }
      } catch (e) {
        console.warn("[OP_REQUEST_ORDER_CONTEXT_CLIENT_FAILED]", {
          message: e?.message || String(e),
        });
        if (!cancelled) setEnrichedPayload(null);
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [valid, hasLinkedOrder, selectedOrder]);

  useEffect(() => {
    if (!valid) {
      console.warn("[OP_REQUEST_FULL_PAGE_MISSING_STATE]", {
        pathname: location.pathname,
        hasState: Boolean(routeState),
      });
      return;
    }
    setRequestType(initialRequestType);
    console.log("[OP_REQUEST_FULL_PAGE_RENDER]", {
      mode,
      requestType: initialRequestType,
      orderId: selectedOrder?.id ?? selectedOrder?.orderId ?? null,
    });
  }, [valid, mode, initialRequestType, selectedOrder, location.pathname, routeState]);

  useEffect(() => {
    if (!valid || nameChangeRestoreRef.current) return;
    const restore = routeState?._nameChangeFormRestore;
    if (!restore || typeof restore !== "object") return;
    nameChangeRestoreRef.current = true;
    setRequestType("name_change");
    if (restore.nameChangePassengerId != null) setNameChangePassengerId(restore.nameChangePassengerId);
    setNameChangeNewFirst(String(restore.nameChangeNewFirst ?? ""));
    setNameChangeNewLast(String(restore.nameChangeNewLast ?? ""));
    setMessage(String(restore.message ?? ""));
  }, [valid, routeState]);

  const displayResolved = useMemo(
    () => (hasLinkedOrder ? resolvePickerOrderDisplayNumber(selectedOrder) : null),
    [hasLinkedOrder, selectedOrder]
  );
  const displayNumber = hasLinkedOrder
    ? displayResolved?.displayNumber
    : GENERAL_ORDER_DISPLAY_LABEL;

  const pageTitle = useMemo(() => {
    if (mode === OPERATION_REQUEST_MODES.payment_link) {
      return `יצירת לינק תשלום עבור הזמנה ${displayNumber}`;
    }
    if (!hasLinkedOrder) {
      const typeLabel =
        QUICK_REQUEST_TYPE_BLOCKS.find((b) => b.value === requestType)?.label || "בירור כללי";
      return `פנייה לאופרציה — ${typeLabel}`;
    }
    return `פנייה לאופרציה עבור הזמנה ${displayNumber}`;
  }, [mode, displayNumber, hasLinkedOrder, requestType]);

  const headerPills = useMemo(() => {
    if (!hasLinkedOrder) {
      return {
        status: "כללי",
        kind: "ללא הזמנה",
        supplierSource: "—",
        dates: "—",
      };
    }
    const os = operationContext?.orderSummary;
    if (!os) {
      return { status: "—", kind: "—", supplierSource: "—", dates: "—" };
    }
    const src = (os.rawSourceType || "").trim();
    const supplierSource =
      src.toUpperCase() === "LDS" || (os.orderKindLabel || "").includes("LDS") ? "LDS" : src || "—";
    return {
      status: os.status || "—",
      kind: os.orderKindLabel || "—",
      supplierSource,
      dates: `${os.checkIn || "—"} – ${os.checkOut || "—"}`,
    };
  }, [hasLinkedOrder, operationContext]);

  const handleBackToOrderPick = useCallback(() => {
    navigate("/p/dashboard/home");
    openOperationRequest();
  }, [navigate, openOperationRequest]);

  const handlePaymentToggle = useCallback((checked) => {
    setPaymentRequired(checked);
    if (!checked) {
      setPaymentDraft(initialPaymentDraft());
    }
  }, []);

  const nameChangeFeePreview =
    requestType === "name_change" ? computePassengerNameChangePayment(usdIlsFromApp) : null;

  const handleGoToYaadPayment = useCallback(async () => {
    if (requestType !== "name_change" || !selectedOrder) return;
    const orderId = String(selectedOrder.id || selectedOrder.orderId || "").trim();
    const passengers = operationContext?.passengers ?? [];
    const sel = passengers.find((p) => p.id === nameChangePassengerId);
    const nf = String(nameChangeNewFirst || "").trim();
    const nl = String(nameChangeNewLast || "").trim();
    if (!sel) {
      errorMessage("נא לבחור נוסע לשינוי שם");
      return;
    }
    if (!nf || !nl) {
      errorMessage("נא למלא שם פרטי ושם משפחה חדשים");
      return;
    }
    const pay = computePassengerNameChangePayment(usdIlsFromApp);
    if (pay.exchangeRateUsed == null || pay.amountIls == null) {
      errorMessage("חסר שער דולר/שקל במערכת. עדכן שער (למשל בלוח הבקרה) ונסה שוב.");
      return;
    }
    if (!orderId) {
      errorMessage("חסר מזהה הזמנה לתשלום");
      return;
    }
    const ok = saveNameChangeOperationDraft({
      routeState: { mode, selectedOrder, requestType: "name_change" },
      form: {
        nameChangePassengerId,
        nameChangeNewFirst,
        nameChangeNewLast,
        message,
      },
    });
    if (!ok) {
      errorMessage("לא ניתן לשמור טיוטה. נסה שוב.");
      return;
    }

    const endpoint = resolveOperationRequestPaymentEndpoint(selectedOrder, mergedOrder);
    const amountIls = Math.round(Number(pay.amountIls) * 100) / 100;
    const body = {
      amount: amountIls,
      actualAmount: amountIls,
      currency: "ILS",
      orderId,
      tash: 1,
      description: NAME_CHANGE_HYP_TRANSACTION_DESCRIPTION,
    };
    try {
      const { getUserDetails } = await import("../../../services/authService.js");
      const uid = getUserDetails?.()?.id;
      if (uid != null) body.agentId = uid;
      else {
        const aid = resolveAgentIdForPaymentBody();
        if (aid != null) body.agentId = aid;
      }
    } catch {
      /* ignore */
    }

    setPaymentLinkBusy(true);
    try {
      const res = await axios.post(`${ApiReqBaseUrl}${endpoint}`, body, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
        validateStatus: () => true,
      });
      const data = res.data;
      const url = extractPrimaryPaymentUrlFromAgentPaymentResponse(data);
      if (res.status >= 200 && res.status < 300 && url) {
        window.open(url, "_blank", "noopener,noreferrer");
        showSuccessToast("קישור תשלום נפתח בחלון חדש");
        return;
      }
      const msg = data?.message || data?.error;
      errorMessage(
        typeof msg === "string" && msg.trim()
          ? msg
          : `שגיאה ביצירת קישור תשלום${res.status ? ` (${res.status})` : ""}`
      );
    } catch {
      errorMessage("שגיאת רשת ביצירת קישור תשלום");
    } finally {
      setPaymentLinkBusy(false);
    }
  }, [
    requestType,
    selectedOrder,
    mergedOrder,
    operationContext,
    nameChangePassengerId,
    nameChangeNewFirst,
    nameChangeNewLast,
    message,
    mode,
    usdIlsFromApp,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!requestType) {
      errorMessage("נא לבחור סוג פנייה");
      return;
    }

    if (!hasLinkedOrder && !isOrderOptionalRequestType(requestType)) {
      errorMessage("סוג פנייה זה דורש בחירת הזמנה");
      return;
    }

    const display = hasLinkedOrder ? resolvePickerOrderDisplayNumber(selectedOrder) : null;
    const orderId = hasLinkedOrder
      ? String(selectedOrder.id || selectedOrder.orderId || "").trim()
      : "";
    const passengers = operationContext?.passengers ?? [];

    let apiPayload = {
      order_id: orderId || null,
      display_order_number: hasLinkedOrder
        ? display?.displayNumber || null
        : GENERAL_ORDER_DISPLAY_LABEL,
      order_type: hasLinkedOrder
        ? selectedOrder.rawOrderType || selectedOrder.orderType || null
        : null,
      order_category: hasLinkedOrder ? selectedOrder.category || null : null,
      request_type: requestType,
    };

    // Paid name-change flow only with a linked order; without order → message-only.
    if (requestType === "name_change" && hasLinkedOrder) {
      const sel = passengers.find((p) => p.id === nameChangePassengerId);
      const nf = String(nameChangeNewFirst || "").trim();
      const nl = String(nameChangeNewLast || "").trim();
      if (!sel) {
        errorMessage("נא לבחור נוסע לשינוי שם");
        return;
      }
      if (!nf || !nl) {
        errorMessage("נא למלא שם פרטי ושם משפחה חדשים");
        return;
      }
      const pay = computePassengerNameChangePayment(usdIlsFromApp);
      if (pay.exchangeRateUsed == null || pay.amountIls == null) {
        errorMessage("חסר שער דולר/שקל במערכת. עדכן שער (למשל בלוח הבקרה) ונסה שוב.");
        return;
      }
      const nameChangeDetail = {
        passengerListId: sel.id,
        displayName: sel.displayName,
        oldFirstName: sel.oldFirstName ?? "",
        oldLastName: sel.oldLastName ?? "",
        newFirstName: nf,
        newLastName: nl,
        roomLabel: sel.roomLabel,
        role: sel.role,
      };
      const finalMessage = buildNameChangeOperationMessage(
        sel,
        nf,
        nl,
        pay.amountUsd,
        pay.exchangeRateUsed,
        pay.amountIls,
        String(message || "").trim()
      );
      logNameChangePaymentTraceDev({
        orderId,
        selectedPassenger: sel,
        oldFirstName: nameChangeDetail.oldFirstName,
        oldLastName: nameChangeDetail.oldLastName,
        newFirstName: nf,
        newLastName: nl,
        amountUsd: pay.amountUsd,
        exchangeRateUsed: pay.exchangeRateUsed,
        amountIls: pay.amountIls,
      });
      apiPayload = {
        ...apiPayload,
        message: finalMessage,
        payment_required: true,
        payment_reason: NAME_CHANGE_PAYMENT_REASON,
        amount_usd: pay.amountUsd,
        amount_ils: pay.amountIls,
        exchange_rate_used: pay.exchangeRateUsed,
        exchange_rate_source: NAME_CHANGE_EXCHANGE_SOURCE,
        name_change_detail: nameChangeDetail,
      };
    } else {
      const trimmedMessage = String(message || "").trim();
      if (!trimmedMessage) {
        errorMessage("נא למלא תיאור הפנייה");
        return;
      }
      apiPayload = {
        ...apiPayload,
        message: trimmedMessage,
        payment_required: hasLinkedOrder ? Boolean(paymentRequired) : false,
      };
    }

    console.log("[OP_REQUEST_CREATE_START]", {
      orderId: orderId || null,
      requestType,
      paymentRequired: apiPayload.payment_required,
      withoutOrder: !hasLinkedOrder,
    });

    setSubmitting(true);
    try {
      const data = await createOperationRequest(apiPayload);
      if (!data?.ok || !data?.request) {
        throw new Error(data?.message || data?.error || "create_failed");
      }
      console.log("[OP_REQUEST_CREATE_SUCCESS]", {
        requestId: data.request.id,
        orderId: data.request.orderId,
        crmLeadId: data.request.crmLeadId || data.request.crm_lead_id || null,
      });

      clearNameChangeOperationDraft();
      await afterOperationRequestCreateSuccess({
        showOperationRequestSuccess,
        navigate,
        request: data.request,
        displayOrderNumber:
          data.request.displayOrderNumber ||
          display?.displayNumber ||
          (!hasLinkedOrder ? GENERAL_ORDER_DISPLAY_LABEL : null),
      });
    } catch (e) {
      const errBody = e?.response?.data;
      console.warn("[OP_REQUEST_CREATE_FAILED]", {
        code: errBody?.error || e?.message,
        status: e?.response?.status,
      });
      errorMessage(errBody?.message || "שליחת הפנייה נכשלה. נסה שוב.");
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedOrder,
    hasLinkedOrder,
    requestType,
    message,
    paymentRequired,
    operationContext,
    nameChangePassengerId,
    nameChangeNewFirst,
    nameChangeNewLast,
    navigate,
    showOperationRequestSuccess,
    usdIlsFromApp,
  ]);

  return (
    <OperationRequestPage
      valid={valid}
      mode={mode}
      mergedOrder={mergedOrder}
      hasLinkedOrder={hasLinkedOrder}
      operationContext={operationContext}
      contextLoading={contextLoading}
      pageTitle={pageTitle}
      headerPills={headerPills}
      requestType={requestType}
      onRequestTypeChange={setRequestType}
      isNameChange={isNameChange}
      passengersForNameChange={passengersForNameChange}
      nameChangePassengerId={nameChangePassengerId}
      onNamePassengerChange={setNameChangePassengerId}
      selectedNamePassenger={selectedNamePassenger}
      nameChangeNewFirst={nameChangeNewFirst}
      nameChangeNewLast={nameChangeNewLast}
      onNameFirstChange={setNameChangeNewFirst}
      onNameLastChange={setNameChangeNewLast}
      nameChangeFeePreview={nameChangeFeePreview}
      paymentRequired={paymentRequired}
      onPaymentToggle={handlePaymentToggle}
      paymentDraft={paymentDraft}
      setPaymentDraft={setPaymentDraft}
      message={message}
      onMessageChange={setMessage}
      onBackToOrderPick={handleBackToOrderPick}
      onSubmitOperation={handleSubmit}
      onGoToPaymentFirst={handleGoToYaadPayment}
      submitting={submitting}
      paymentLinkBusy={paymentLinkBusy}
    />
  );
}
